import React from "react";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  PhoneCall,
  User,
  Mail,
  Phone,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type HelpFor = "Myself" | "Someone I love" | "";
export type Concern =
  | "Depression"
  | "Anxiety"
  | "PTSD"
  | "Depression + Anxiety"
  | "I'm not sure"
  | "";

const CONCERN_OPTIONS: { label: Concern; icon: string }[] = [
  { label: "Depression", icon: "😔" },
  { label: "Anxiety", icon: "😰" },
  { label: "PTSD", icon: "🧠" },
  { label: "Depression + Anxiety", icon: "⚡" },
  { label: "I'm not sure", icon: "❓" },
];

const formatPhone = (raw: string) => {
  const d = raw.replace(/[^\d]/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/* ── Step 1: Who ── */
export const WhoStep: React.FC<{
  onSelect: (v: HelpFor) => void;
}> = ({ onSelect }) => (
  <div className="px-6 py-8 sm:px-10 sm:py-10">
    <div className="mb-6 text-xs font-semibold uppercase tracking-wider text-accent">
      Step 1 of 3
    </div>
    <h2 className="font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl">
      Who are you looking for help for?
    </h2>
    <p className="mt-2 text-sm text-muted-foreground sm:text-base">
      There's no obligation — we'll just help you understand your options.
    </p>
    <div className="mt-6 space-y-3">
      {[
        {
          v: "Myself" as HelpFor,
          icon: User,
          sub: "I'm exploring treatment for my own symptoms",
        },
        {
          v: "Someone I love" as HelpFor,
          icon: Heart,
          sub: "I'm helping a family member or friend",
        },
      ].map((opt) => (
        <button
          key={opt.v}
          onClick={() => onSelect(opt.v)}
          className="group flex w-full items-center gap-4 rounded-2xl border-2 border-border bg-card p-5 text-left transition-all hover:border-accent hover:shadow-md focus:outline-hidden focus:ring-2 focus:ring-accent/40"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent transition-colors group-hover:bg-accent/20">
            <opt.icon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="font-headings text-lg font-bold text-primary">
              {opt.v}
            </p>
            <p className="text-sm text-muted-foreground">{opt.sub}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-accent" />
        </button>
      ))}
    </div>
  </div>
);

/* ── Step 2: Concern ── */
export const ConcernStep: React.FC<{
  onSelect: (v: Concern) => void;
  onBack: () => void;
}> = ({ onSelect, onBack }) => (
  <div className="px-6 py-8 sm:px-10 sm:py-10">
    <div className="mb-6 flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-accent">
        Step 2 of 3
      </span>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
    </div>
    <h2 className="font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl">
      What would you like help with?
    </h2>
    <p className="mt-2 text-sm text-muted-foreground sm:text-base">
      Select what best describes what you're experiencing.
    </p>
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {CONCERN_OPTIONS.map((opt) => (
        <button
          key={opt.label}
          onClick={() => onSelect(opt.label)}
          className="group flex items-center gap-3 rounded-2xl border-2 border-border bg-card p-4 text-left transition-all hover:border-accent/50 hover:shadow-md focus:outline-hidden focus:ring-2 focus:ring-accent/40"
        >
          <span className="text-2xl">{opt.icon}</span>
          <span className="flex-1 font-headings text-sm font-bold text-primary sm:text-base">
            {opt.label}
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
        </button>
      ))}
    </div>
  </div>
);

/* ── Step 3: Contact ── */
export const ContactStep: React.FC<{
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setPhone: (v: string) => void;
  setEmail: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  status: "idle" | "submitting";
  error: string | null;
}> = ({
  firstName,
  lastName,
  phone,
  email,
  setFirstName,
  setLastName,
  setPhone,
  setEmail,
  onBack,
  onSubmit,
  status,
  error,
}) => (
  <div className="px-6 py-8 sm:px-10 sm:py-10">
    <div className="mb-6 flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-accent">
        Step 3 of 3
      </span>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
    </div>
    <h2 className="font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl">
      Where should we send your information?
    </h2>
    <p className="mt-2 text-sm text-muted-foreground sm:text-base">
      We'll send you resources about SAINT® TMS and help you understand your
      insurance options. No obligation to begin treatment.
    </p>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="mt-6 space-y-4"
      noValidate
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label
            htmlFor="quiz-first"
            className="text-sm font-semibold text-primary"
          >
            First Name
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="quiz-first"
              type="text"
              autoComplete="given-name"
              placeholder="Jane"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="pl-9"
              disabled={status === "submitting"}
              required
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label
            htmlFor="quiz-last"
            className="text-sm font-semibold text-primary"
          >
            Last Name
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="quiz-last"
              type="text"
              autoComplete="family-name"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="pl-9"
              disabled={status === "submitting"}
              required
            />
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor="quiz-phone"
          className="text-sm font-semibold text-primary"
        >
          Phone Number
        </Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            id="quiz-phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="(954) 555-1234"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            className="pl-9"
            disabled={status === "submitting"}
            required
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor="quiz-email"
          className="text-sm font-semibold text-primary"
        >
          Email Address
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            id="quiz-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-9"
            disabled={status === "submitting"}
            required
          />
        </div>
      </div>
      {error && (
        <p className="text-sm font-medium text-destructive" role="alert">
          {error}
        </p>
      )}
      <Button
        type="submit"
        size="lg"
        disabled={status === "submitting"}
        className="w-full bg-gradient-accent font-bold text-accent-foreground shadow-glow hover:opacity-90"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending your information…
          </>
        ) : (
          <>
            Get My SAINT® Info & Resources
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        By submitting, you're requesting information about SAINT® TMS and
        insurance options. Coverage and treatment eligibility are not
        guaranteed. We respect your privacy.
      </p>
    </form>
  </div>
);

/* ── Step 4: Success ── */
export const SuccessStep: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="px-6 py-10 text-center sm:px-10 sm:py-12">
    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent/15 text-accent">
      <CheckCircle2 className="h-9 w-9" />
    </div>
    <h2 className="font-headings text-2xl font-extrabold text-primary sm:text-3xl">
      We Received Your Request
    </h2>
    <p className="mx-auto mt-3 max-w-md text-foreground/80 leading-relaxed">
      Our team will review your information and reach out with resources about
      SAINT® TMS and your insurance options.
    </p>
    <div className="mx-auto mt-6 max-w-sm rounded-2xl border border-border bg-muted/40 p-5 text-left">
      <p className="mb-3 font-headings text-sm font-bold uppercase tracking-wider text-primary">
        What Happens Next
      </p>
      <ol className="space-y-2.5 text-sm text-foreground/80">
        <li className="flex gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
            1
          </span>
          <span>We review the information you provided.</span>
        </li>
        <li className="flex gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
            2
          </span>
          <span>We send you SAINT® resources and check your insurance.</span>
        </li>
        <li className="flex gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
            3
          </span>
          <span>If appropriate, you can schedule a private consultation.</span>
        </li>
      </ol>
    </div>
    <div className="mt-6">
      <p className="text-sm text-muted-foreground">Prefer to talk now?</p>
      <a
        href="tel:9548279793"
        className="mt-2 inline-flex items-center gap-2 font-headings text-lg font-bold text-accent hover:underline"
      >
        <PhoneCall className="h-5 w-5" /> 954-827-9793
      </a>
    </div>
    <Button
      onClick={onClose}
      variant="outline"
      className="mt-6 w-full sm:w-auto"
    >
      Continue Exploring
    </Button>
  </div>
);
